import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Play, RefreshCw, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, Clock, Circle, AlertTriangle, Loader2, Upload, Trash2, ToggleLeft, ToggleRight
} from 'lucide-react';

// ── Status badge component ──
function StatusBadge({ status }) {
  const styles = {
    pass:  'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    fail:  'bg-red-500/15 text-red-400 border-red-500/30',
    skip:  'bg-slate-500/15 text-slate-400 border-slate-500/30',
    running: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    pending: 'bg-slate-500/15 text-slate-500 border-slate-500/30'
  };
  const icons = {
    pass: <CheckCircle2 className="w-3 h-3" />,
    fail: <XCircle className="w-3 h-3" />,
    skip: <Circle className="w-3 h-3" />,
    running: <Loader2 className="w-3 h-3 animate-spin" />,
    pending: <Clock className="w-3 h-3" />
  };
  const s = status || 'pending';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${styles[s] || styles.pending}`}>
      {icons[s] || icons.pending} {s}
    </span>
  );
}

// ── Layer badge ──
function LayerBadge({ layer }) {
  const isAPI = layer === 'API' || layer === 'L1';
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
      isAPI
        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
        : 'bg-amber-500/10 text-amber-400 border-amber-500/25'
    }`}>
      {isAPI ? 'API' : 'BROWSER'}
    </span>
  );
}

// ── Time ago helper ──
function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  return days + 'd ago';
}

// ── Duration formatter ──
function formatDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return ms + 'ms';
  return (ms / 1000).toFixed(1) + 's';
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function TesterCasterPanel() {
  const queryClient = useQueryClient();
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [expandedRun, setExpandedRun] = useState(null);
  const [expandedTest, setExpandedTest] = useState(null);
  const [runningIds, setRunningIds] = useState(null); // test_ids currently running
  const [markingDeployed, setMarkingDeployed] = useState(false);
  const [deletingTest, setDeletingTest] = useState(null);
  const [togglingTest, setTogglingTest] = useState(null);
  const [deletingRuns, setDeletingRuns] = useState(false);
  const [selectedRuns, setSelectedRuns] = useState(new Set());

  // Toggle test active/inactive
  const toggleTestActive = async (tc) => {
    setTogglingTest(tc.test_id);
    try {
      await base44.entities.TestCase.update(tc.id, { is_active: !tc.is_active });
      queryClient.invalidateQueries({ queryKey: ['testCases'] });
    } catch (err) {
      alert('Toggle failed: ' + err.message);
    } finally {
      setTogglingTest(null);
    }
  };

  // Delete test case
  const deleteTest = async (tc) => {
    if (!confirm('Delete test ' + tc.test_id + '? This cannot be undone.')) return;
    setDeletingTest(tc.test_id);
    try {
      await base44.entities.TestCase.delete(tc.id);
      queryClient.invalidateQueries({ queryKey: ['testCases'] });
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setDeletingTest(null);
    }
  };

  // Delete selected runs
  const deleteSelectedRuns = async () => {
    if (selectedRuns.size === 0) return;
    if (!confirm('Delete ' + selectedRuns.size + ' run(s)? This cannot be undone.')) return;
    setDeletingRuns(true);
    try {
      var runsToDelete = testRuns.filter(r => selectedRuns.has(r.run_id));
      for (var i = 0; i < runsToDelete.length; i++) {
        await base44.entities.TestRun.delete(runsToDelete[i].id);
      }
      queryClient.invalidateQueries({ queryKey: ['testRuns'] });
      setSelectedRuns(new Set());
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setDeletingRuns(false);
    }
  };

  // Mark stale "running" runs as failed
  const fixStaleRun = async (run) => {
    try {
      await base44.entities.TestRun.update(run.id, {
        status: 'failed',
        summary: (run.summary || '') + '\n\n[Manually marked as failed — run was stuck in "running" state]',
        completed_at: new Date().toISOString()
      });
      queryClient.invalidateQueries({ queryKey: ['testRuns'] });
    } catch (err) {
      alert('Fix failed: ' + err.message);
    }
  };

  // ── Data queries ──
  const { data: testCases = [], isLoading: loadingTests } = useQuery({
    queryKey: ['testCases'],
    queryFn: () => base44.entities.TestCase.list()
  });

  const { data: testRuns = [], isLoading: loadingRuns } = useQuery({
    queryKey: ['testRuns'],
    queryFn: () => base44.entities.TestRun.list('-run_created_at')
  });

  // Mark all targets as freshly deployed (single backend call)
  const markAllDeployed = async () => {
    setMarkingDeployed(true);
    try {
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'mark_deployed',
        all: true
      });
      var data = result?.data || result;
      alert((data.updated || 0) + ' targets marked as deployed.');
    } catch (err) {
      alert('Mark deployed failed: ' + err.message);
    } finally {
      setMarkingDeployed(false);
    }
  };

  // ── Run tests mutation ──
  const runMutation = useMutation({
    mutationFn: (payload) => base44.functions.invoke('testerCasterRunTests', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['testCases'] });
      queryClient.invalidateQueries({ queryKey: ['testRuns'] });
      setRunningIds(null);

      // If run paused (budget exhausted), auto-resume
      const data = result?.data || result;
      if (data?.next_invocation) {
        setRunningIds(data.next_invocation.test_ids || []);
        setTimeout(() => {
          runMutation.mutate(data.next_invocation);
        }, 1000);
      }
    },
    onError: () => setRunningIds(null)
  });

  // ── Derived data ──
  const sortedTests = useMemo(() => {
    return [...testCases].sort((a, b) => {
      // API first, then BROWSER
      const layerOrder = { API: 0, L1: 0, BROWSER: 1 };
      const la = layerOrder[a.layer] ?? 2;
      const lb = layerOrder[b.layer] ?? 2;
      if (la !== lb) return la - lb;
      // Then by test_id
      return (a.test_id || '').localeCompare(b.test_id || '');
    });
  }, [testCases]);

  const apiTests = sortedTests.filter(t => t.layer === 'API' || t.layer === 'L1');
  const browserTests = sortedTests.filter(t => t.layer === 'BROWSER');
  const activeTests = sortedTests.filter(t => t.is_active);
  const passCount = activeTests.filter(t => t.last_result === 'pass').length;
  const failCount = activeTests.filter(t => t.last_result === 'fail').length;
  const untestedCount = activeTests.filter(t => !t.last_result).length;
  const recentRuns = (testRuns || []).slice(0, 10);

  // ── Handlers ──
  const toggleSelect = (testId) => {
    setSelectedTests(prev => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  const selectAllAPI = () => {
    const apiIds = apiTests.filter(t => t.is_active).map(t => t.test_id);
    setSelectedTests(new Set(apiIds));
  };

  const clearSelection = () => setSelectedTests(new Set());

  const runSelected = () => {
    const ids = Array.from(selectedTests);
    if (ids.length === 0) return;
    setRunningIds(ids);
    runMutation.mutate({ test_ids: ids });
  };

  const runAllAPI = () => {
    const ids = apiTests.filter(t => t.is_active).map(t => t.test_id);
    setRunningIds(ids);
    runMutation.mutate({ test_ids: ids });
  };

  const isRunning = runMutation.isPending || !!runningIds;

  // ── Parse run results safely ──
  const parseResults = (run) => {
    if (!run?.results) return [];
    try {
      return typeof run.results === 'string' ? JSON.parse(run.results) : run.results;
    } catch { return []; }
  };

  if (loadingTests && loadingRuns) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading TesterCaster...
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── KPI BAR ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Total Tests</div>
          <div className="text-2xl font-extrabold text-cyan-400">{activeTests.length}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{apiTests.length} API · {browserTests.length} browser</div>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Passing</div>
          <div className="text-2xl font-extrabold text-emerald-400">{passCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{activeTests.length > 0 ? Math.round(passCount / activeTests.length * 100) : 0}% pass rate</div>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Failing</div>
          <div className={`text-2xl font-extrabold ${failCount > 0 ? 'text-red-400' : 'text-slate-600'}`}>{failCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{failCount > 0 ? 'action needed' : 'all clear'}</div>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Untested</div>
          <div className={`text-2xl font-extrabold ${untestedCount > 0 ? 'text-amber-400' : 'text-slate-600'}`}>{untestedCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{browserTests.length} need Playwright</div>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Last Run</div>
          <div className="text-lg font-bold text-slate-300">{recentRuns.length > 0 ? timeAgo(recentRuns[0]?.run_created_at) : '—'}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{recentRuns.length} total runs</div>
        </div>
      </div>

      {/* ── CONTROL BAR ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={runAllAPI}
          disabled={isRunning}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
        >
          {isRunning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
          Run All API Tests
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={runSelected}
          disabled={isRunning || selectedTests.size === 0}
          className="border-cyan-500/40 text-cyan-300 bg-cyan-500/10 text-xs hover:bg-cyan-500/20"
        >
          <Play className="w-3.5 h-3.5 mr-1.5" />
          Run Selected ({selectedTests.size})
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={deleteSelectedRuns}
          disabled={deletingRuns || selectedRuns.size === 0}
          className="text-red-400/60 text-xs hover:text-red-400 hover:bg-red-500/10"
        >
          {deletingRuns
            ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Deleting...</>
            : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Runs ({selectedRuns.size})</>
          }
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={selectAllAPI}
          className="text-slate-400 text-xs hover:text-white"
        >
          Select all API
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={clearSelection}
          className="text-slate-500 text-xs hover:text-white"
        >
          Clear
        </Button>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['testCases'] });
            queryClient.invalidateQueries({ queryKey: ['testRuns'] });
          }}
          className="text-slate-500 text-xs hover:text-white"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={markAllDeployed}
          disabled={markingDeployed}
          className="text-slate-500 text-xs hover:text-white"
        >
          {markingDeployed
            ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Marking...</>
            : <><Upload className="w-3.5 h-3.5 mr-1" /> Mark Deployed</>
          }
        </Button>
      </div>

      {/* ── RUNNING INDICATOR ── */}
      {isRunning && (
        <div className="bg-cyan-500/10 border border-cyan-500/25 rounded-lg px-4 py-3 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
          <span className="text-sm text-cyan-300 font-medium">
            Running {runningIds ? runningIds.join(', ') : 'tests'}...
          </span>
        </div>
      )}

      {/* ── PENDING RUNS (from Verify Fix) ── */}
      {(() => {
        const pendingRuns = testRuns.filter(r => r.status === 'pending');
        if (pendingRuns.length === 0) return null;
        return (
          <div className="space-y-2">
            {pendingRuns.map(run => (
              <div key={run.id || run.run_id} className="bg-purple-500/8 border border-purple-500/25 rounded-lg px-4 py-3 flex items-center gap-3">
                <Clock className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-purple-300 font-semibold">{run.run_id}</span>
                  {run.bug_id && (
                    <span className="text-[11px] text-purple-400/70 ml-2">Bug #{run.bug_id}</span>
                  )}
                  {run.fix_description && (
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">{run.fix_description.slice(0, 120)}</p>
                  )}
                </div>
                <span className="text-[10px] text-slate-500">{run.test_ids ? (Array.isArray(run.test_ids) ? run.test_ids.length : JSON.parse(run.test_ids || '[]').length) : '?'} tests</span>
                <Button
                  size="sm"
                  onClick={() => {
                    const ids = run.test_ids
                      ? (Array.isArray(run.test_ids) ? run.test_ids : JSON.parse(run.test_ids || '[]'))
                      : [];
                    if (ids.length === 0) return;
                    setRunningIds(ids);
                    runMutation.mutate({ run_id: run.run_id, test_ids: ids });
                  }}
                  disabled={isRunning}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                >
                  <Play className="w-3.5 h-3.5 mr-1" /> Run Now
                </Button>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── TEST MATRIX ── */}
      <Card className="bg-slate-900/40 border-slate-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Test Matrix</h3>
          <span className="text-[10px] text-slate-500 uppercase tracking-widest">{sortedTests.length} test cases</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800/80">
                <th className="w-8 px-3 py-2.5"></th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">ID</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Name</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Target</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Layer</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Tags</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Last Result</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Last Run</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Failures</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-widest text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* API Tests Section */}
              <tr><td colSpan="9" className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/5 border-b border-emerald-500/15">API Tests ({apiTests.length})</td></tr>
              {apiTests.map(tc => (
                <tr
                  key={tc.test_id}
                  className={`border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors cursor-pointer ${
                    selectedTests.has(tc.test_id) ? 'bg-cyan-500/5' : ''
                  } ${!tc.is_active ? 'opacity-40' : ''}`}
                  onClick={() => tc.is_active && toggleSelect(tc.test_id)}
                >
                  <td className="px-3 py-2">
                    <input data-testid="fb-testercasterpanel-input"
                      type="checkbox"
                      checked={selectedTests.has(tc.test_id)}
                      onChange={() => toggleSelect(tc.test_id)}
                      disabled={!tc.is_active}
                      className="rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-cyan-400">{tc.test_id}</td>
                  <td className="px-3 py-2 text-xs text-slate-300">{tc.name}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-400 font-mono">{tc.target || '—'}</td>
                  <td className="px-3 py-2"><LayerBadge layer={tc.layer} /></td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(tc.tags) ? tc.tags : []).slice(0, 3).map(function(tag, i) {
                        return <span key={i} className="px-1.5 py-0.5 rounded text-[9px] bg-slate-800 text-slate-500 border border-slate-700">{tag}</span>;
                      })}
                      {(Array.isArray(tc.tags) ? tc.tags : []).length > 3 && <span className="text-[9px] text-slate-600">+{tc.tags.length - 3}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={tc.last_result} /></td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">{timeAgo(tc.last_run_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    {tc.failure_count > 0
                      ? <span className="text-red-400 font-semibold">{tc.failure_count}</span>
                      : <span className="text-slate-600">0</span>
                    }
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleTestActive(tc); }}
                        disabled={togglingTest === tc.test_id}
                        className="p-1 rounded hover:bg-slate-700/50 transition-colors"
                        title={tc.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {togglingTest === tc.test_id
                          ? <Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" />
                          : tc.is_active
                            ? <ToggleRight className="w-3.5 h-3.5 text-emerald-400" />
                            : <ToggleLeft className="w-3.5 h-3.5 text-slate-600" />
                        }
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteTest(tc); }}
                        disabled={deletingTest === tc.test_id}
                        className="p-1 rounded hover:bg-red-500/20 transition-colors"
                        title="Delete test"
                      >
                        {deletingTest === tc.test_id
                          ? <Loader2 className="w-3.5 h-3.5 text-red-400 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5 text-slate-600 hover:text-red-400" />
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Browser Tests Section */}
              <tr><td colSpan="9" className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-400 bg-amber-500/5 border-b border-amber-500/15">Browser Tests ({browserTests.length})</td></tr>
              {browserTests.map(tc => (
                <tr
                  key={tc.test_id}
                  className="border-b border-slate-800/50 opacity-40"
                >
                  <td className="px-3 py-2">
                    <input data-testid="fb-testercasterpanel-input-1" type="checkbox" disabled className="rounded border-slate-700 bg-slate-800" />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-500">{tc.test_id}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{tc.name}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-600 font-mono">{tc.target || '—'}</td>
                  <td className="px-3 py-2"><LayerBadge layer={tc.layer} /></td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(tc.tags) ? tc.tags : []).slice(0, 3).map(function(tag, i) {
                        return <span key={i} className="px-1.5 py-0.5 rounded text-[9px] bg-slate-800/50 text-slate-600 border border-slate-700/50">{tag}</span>;
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-2"><span className="text-[10px] text-slate-600 uppercase">needs playwright</span></td>
                  <td className="px-3 py-2 text-[11px] text-slate-600">—</td>
                  <td className="px-3 py-2 text-xs text-slate-600">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── RUN HISTORY ── */}
      <Card className="bg-slate-900/40 border-slate-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Run History</h3>
          <span className="text-[10px] text-slate-500 uppercase tracking-widest">{recentRuns.length} runs</span>
        </div>
        {recentRuns.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-500 text-sm">No test runs yet</div>
        ) : (
          <div className="divide-y divide-slate-800/60">
            {recentRuns.map(run => {
              const results = parseResults(run);
              const isExpanded = expandedRun === run.run_id;
              const acceptIds = run.acceptance_test_ids && Array.isArray(run.acceptance_test_ids)
                ? run.acceptance_test_ids
                : run.acceptance_test_id ? [run.acceptance_test_id] : [];
              const hasAcceptance = acceptIds.length > 0;
              // Detect stale "running" — if running for more than 5 minutes, it's stuck
              const isStaleRunning = run.status === 'running' && run.run_created_at &&
                (Date.now() - new Date(run.run_created_at).getTime()) > 5 * 60 * 1000;
              const statusColor = isStaleRunning ? 'text-amber-400'
                : run.status === 'passed' ? 'text-emerald-400'
                : run.status === 'failed' ? 'text-red-400'
                : 'text-amber-400';
              const isRunSelected = selectedRuns.has(run.run_id);
              return (
                <div key={run.id || run.run_id}>
                  <div
                    className="px-4 py-3 flex items-center gap-3 hover:bg-slate-800/30 cursor-pointer transition-colors"
                    onClick={() => setExpandedRun(isExpanded ? null : run.run_id)}
                  >
                    <input data-testid="fb-testercasterpanel-input-2"
                      type="checkbox"
                      checked={isRunSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedRuns(prev => {
                          const next = new Set(prev);
                          if (next.has(run.run_id)) next.delete(run.run_id);
                          else next.add(run.run_id);
                          return next;
                        });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500/30 w-3.5 h-3.5 flex-shrink-0"
                    />
                    {isExpanded
                      ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    }
                    <span className="font-mono text-xs text-cyan-400 font-semibold w-32 flex-shrink-0">{run.run_id}</span>
                    <span className={`text-xs font-bold uppercase ${statusColor}`}>
                      {isStaleRunning ? 'STUCK' : run.status}
                    </span>
                    {isStaleRunning && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); fixStaleRun(run); }}
                        className="text-amber-400 text-[10px] h-5 px-2 hover:bg-amber-500/20 border border-amber-500/20"
                      >
                        Mark Failed
                      </Button>
                    )}
                    {run.bug_id && (
                      <span className="text-[10px] text-purple-400 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded">Bug #{run.bug_id}</span>
                    )}
                    {hasAcceptance && (
                      <span className="text-[10px] text-violet-400 bg-violet-500/10 border border-violet-500/20 px-1.5 py-0.5 rounded">🎯 {acceptIds.length} acceptance</span>
                    )}
                    <span className="text-[11px] text-slate-500">
                      {run.tests_passed || 0}✓ {run.tests_failed || 0}✗ {run.tests_skipped || 0}—
                    </span>
                    <span className="text-[11px] text-slate-600">{formatDuration(run.total_duration_ms)}</span>
                    <div className="flex-1" />
                    <span className="text-[11px] text-slate-600">{timeAgo(run.run_created_at)}</span>
                  </div>

                  {/* Expanded run details */}
                  {isExpanded && (
                    <div className="px-4 pb-3 bg-slate-900/40">
                      {run.summary && (
                        <pre className="text-[11px] text-slate-400 bg-slate-950/60 border border-slate-800 rounded p-3 mb-3 overflow-x-auto whitespace-pre-wrap font-mono">{run.summary}</pre>
                      )}
                      {run.failure_report && (
                        <div className="mb-3">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Failure Report
                          </div>
                          <pre className="text-[11px] text-red-300/80 bg-red-500/5 border border-red-500/15 rounded p-3 overflow-x-auto whitespace-pre-wrap font-mono">{run.failure_report}</pre>
                        </div>
                      )}
                      {results.length > 0 && (
                        <div className="space-y-1.5">
                          {results.map(r => (
                            <div key={r.test_id}>
                              <div
                                className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-800/40 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedTest(expandedTest === r.test_id + run.run_id ? null : r.test_id + run.run_id);
                                }}
                              >
                                {expandedTest === r.test_id + run.run_id
                                  ? <ChevronDown className="w-3 h-3 text-slate-500" />
                                  : <ChevronRight className="w-3 h-3 text-slate-500" />
                                }
                                <span className="font-mono text-[11px] text-cyan-400 font-semibold">{r.test_id}</span>
                                {hasAcceptance && acceptIds.indexOf(r.test_id) !== -1 && (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-violet-500/15 text-violet-400 border border-violet-500/25">Acceptance</span>
                                )}
                                {hasAcceptance && acceptIds.indexOf(r.test_id) === -1 && (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-500/10 text-slate-500 border border-slate-500/20">Regression</span>
                                )}
                                <StatusBadge status={r.status} />
                                <span className="text-[11px] text-slate-600">{formatDuration(r.duration_ms)}</span>
                                {r.assertions && (
                                  <span className="text-[10px] text-slate-500">
                                    {r.assertions.filter(a => a.passed).length}/{r.assertions.length} assertions
                                  </span>
                                )}
                              </div>
                              {/* Per-assertion detail */}
                              {expandedTest === r.test_id + run.run_id && r.assertions && (
                                <div className="ml-8 mb-2 space-y-0.5">
                                  {r.assertions.map((a, i) => (
                                    <div key={i} className="flex items-center gap-2 text-[11px] py-0.5">
                                      {a.passed
                                        ? <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                                        : <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                                      }
                                      <span className="text-slate-500">Step {a.step}:</span>
                                      <span className={`font-mono ${a.passed ? 'text-slate-400' : 'text-red-300'}`}>
                                        {a.field} <span className="text-slate-600">{a.op}</span>
                                        {a.expected ? <> <span className="text-slate-500">{typeof a.expected === 'string' && a.expected.length > 40 ? a.expected.slice(0, 40) + '…' : a.expected}</span></> : null}
                                      </span>
                                      {!a.passed && (
                                        <span className="text-red-400/70 text-[10px] ml-1">
                                          got: {typeof a.actual === 'string' && a.actual.length > 60 ? a.actual.slice(0, 60) + '…' : a.actual}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}